"use client";

import { Button } from "@/_components/lib/ui/button";
import { ATSJobStatus } from "@/_services/resumeServices/ats-resume";
import { useThemeColorScope } from "@/_theme/useThemeColorScope";
import {
  Box,
  Center,
  Dialog,
  Flex,
  HStack,
  Icon,
  Portal,
  Progress,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { LuCheck, LuFileOutput, LuScanSearch, LuTags } from "react-icons/lu";
import { KnootLottie } from "../../lotties/Lottie";

const ATS_STEP_KEYS = ["analyze", "standardize", "export"] as const;

const STEP_ICONS = {
  analyze: LuScanSearch,
  standardize: LuTags,
  export: LuFileOutput,
} as const;

export interface GenerateATSResumeDialogProps {
  open: boolean;
  status: ATSJobStatus | null;
  progress: number;
  activeStepIndex: number;
  onCancel: () => void;
  onRetry: () => void;
}

export const GenerateATSResumeDialog = ({
  open,
  status,
  progress,
  activeStepIndex,
  onCancel,
  onRetry,
}: GenerateATSResumeDialogProps) => {
  const t = useTranslations(
    "resume_community.talent_pool.resume_detail.resume.ats_generate.dialog",
  );
  const themeColorScope = useThemeColorScope();

  const isProcessing = status === ATSJobStatus.PROCESSING || status === null;
  const isFailed = status === ATSJobStatus.FAILED;
  const isCompleted = status === ATSJobStatus.COMPLETED;

  const stepIconStyles = useMemo(
    () =>
      ATS_STEP_KEYS.map((_, index) => {
        const isDone = isCompleted || index < activeStepIndex;
        const isActive = isProcessing && index === activeStepIndex;

        let bg = "themeColor.blur.alpha900";
        let color = "themeColor.fg.title";

        if (isDone) {
          bg = "themeColor.status.successBg";
          color = "themeColor.status.successFg";
        } else if (isActive) {
          bg = "themeColor.status.primaryBg";
          color = "themeColor.status.primaryFg";
        }

        return {
          isDone,
          isActive,
          bg,
          color,
        };
      }),
    [activeStepIndex, isCompleted, isProcessing],
  );

  const dialogFooter = useMemo(() => {
    if (isFailed) {
      return (
        <HStack gap={3}>
          <Button
            visual="solid"
            colorPalette="primary"
            {...themeColorScope}
            onClick={onRetry}
          >
            {t("retry")}
          </Button>
          <Button
            visual="ghost"
            colorPalette="dark"
            color="themeColor.fg.title"
            border="1px solid"
            borderColor="themeColor.border.muted"
            _hover={{
              bg: "themeColor.bg.surface",
              color: "themeColor.status.errorFg",
              borderColor: "themeColor.status.errorBorder",
            }}
            {...themeColorScope}
            onClick={onCancel}
          >
            {t("close")}
          </Button>
        </HStack>
      );
    } else {
      return (
        <Button
          visual="none-style"
          color="themeColor.fg.title"
          border="1px solid"
          borderColor="themeColor.border.muted"
          _hover={{
            bg: "themeColor.bg.surface",
            color: "themeColor.status.errorFg",
            borderColor: "themeColor.status.errorBorder",
          }}
          {...themeColorScope}
          onClick={onCancel}
          minW="32"
        >
          {t("cancel")}
        </Button>
      );
    }
  }, [isFailed, onCancel, onRetry, t]);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={({ open: isOpen }) => {
        if (!isOpen && !isProcessing) {
          onCancel();
        }
      }}
      placement="center"
      size="md"
      closeOnEscape={!isProcessing}
      closeOnInteractOutside={false}
    >
      <Portal>
        <Dialog.Backdrop bg="blackAlpha.400" backdropFilter="blur(2px)" />
        <Dialog.Positioner>
          <Dialog.Content
            {...themeColorScope}
            maxW="md"
            rounded="2xl"
            bg="themeColor.bg.surface"
            p={0}
            overflow="hidden"
            shadow="themeColor.shadow.lg"
          >
            <Dialog.Header
              pt={8}
              pb={4}
              px={8}
              textAlign="center"
              flexDir="column"
              alignItems="center"
            >
              <Center mb="-12px">
                <KnootLottie
                  animationName="generatingATS"
                  height="140px"
                  width="140px"
                />
              </Center>
              <Dialog.Title
                fontSize="xl"
                fontWeight="bold"
                color="themeColor.fg.title"
                mb={2}
              >
                {isFailed ? t("failed_title") : t("title")}
              </Dialog.Title>
              <Text
                fontSize="sm"
                color="themeColor.fg.muted"
                fontWeight="normal"
              >
                {isFailed ? t("failed_body") : t("subtitle")}
              </Text>
            </Dialog.Header>

            <Dialog.Body px={8} pb={6}>
              {!isFailed && (
                <Box mb={6}>
                  <Progress.Root
                    value={isCompleted ? 100 : progress}
                    colorPalette="primary"
                    size="sm"
                    striped={isProcessing}
                    animated={isProcessing}
                  >
                    <Progress.Track
                      rounded="full"
                      bg="themeColor.status.primaryBg"
                    >
                      <Progress.Range />
                    </Progress.Track>
                  </Progress.Root>
                </Box>
              )}

              {!isFailed && (
                <VStack align="stretch" gap={3}>
                  {ATS_STEP_KEYS.map((stepKey, index) => {
                    const StepIcon = STEP_ICONS[stepKey];
                    const { isDone, isActive, bg, color } =
                      stepIconStyles[index];

                    return (
                      <HStack key={stepKey} gap={3} align="center">
                        <Center
                          boxSize="8"
                          rounded="lg"
                          bg={bg}
                          color={color}
                          flexShrink={0}
                        >
                          {isDone ? (
                            <Icon as={LuCheck} boxSize={4} />
                          ) : isActive ? (
                            <Spinner
                              size="sm"
                              color="themeColor.fg.brandStrong"
                            />
                          ) : (
                            <Icon as={StepIcon} boxSize={4} />
                          )}
                        </Center>
                        <Flex flex="1" align="center" minW={0}>
                          <Text
                            fontSize="sm"
                            color={
                              isDone || isActive
                                ? "themeColor.fg.strong"
                                : "themeColor.fg.muted"
                            }
                            fontWeight={isActive ? "medium" : "normal"}
                          >
                            {t(`steps.${stepKey}`)}
                          </Text>
                        </Flex>
                      </HStack>
                    );
                  })}
                </VStack>
              )}
            </Dialog.Body>

            <Dialog.Footer px={8} pb={8} pt={0} justifyContent="center">
              {dialogFooter}
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
